import { CONFIG } from './config';
import { isReleaseOnTarget } from './layout';
import type { PlacementTarget, PlanetId, Point } from './types';

/**
 * The ONE reusable interaction module for this activity (PRP §Project Structure).
 * Owns: pointer capture, lift offset, tap-select/deselect, release -> snap or
 * ease-back. Does NOT own: scoring, narration, planet identity semantics,
 * prompts, or anything prompt-state-machine-shaped.
 *
 * Two first-class input modes share the same validation path (PRP AC-07):
 *   - DRAG: pointer-down on a draggable -> pointer-move -> pointer-up.
 *   - TAP-TAP: pointer-down+up on a draggable with no movement selects it; a
 *     subsequent tap on a target attempts placement.
 */

export interface DraggableHandle {
  id: PlanetId;
  element: HTMLElement;
  /** Position before drag, used as the ease-back destination on a miss. */
  home: Point;
  /** Half of the wrapper's hit area (>=48px); precomputed at attach time so
   *   the pointermove hot path never reads layout (PRP §Performance). */
  halfSize: number;
}

export interface TargetHandle {
  id: PlanetId;
  target: PlacementTarget;
}

export interface CreateDragOrTapOptions {
  draggables: DraggableHandle[];
  targets: TargetHandle[];
  /** Callback to look up the live pointer position relative to the scene. */
  scenePoint: (clientX: number, clientY: number) => Point;
  /** Apply a visual transform during drag. */
  onMove: (id: PlanetId, scenePoint: Point, mode: 'lifted' | 'selected' | 'rest') => void;
  /** Validate a candidate release against the targets (default uses isReleaseOnTarget). */
  validate?: (
    draggableId: PlanetId,
    release: Point,
    targets: TargetHandle[],
  ) => PlanetId | null;
  /** A correct placement occurred. */
  onPlaced: (draggableId: PlanetId, targetId: PlanetId, centre: Point) => void;
  /** An incorrect/missed placement occurred; the draggable returns home. */
  onReturned: (draggableId: PlanetId) => void;
  /** A draggable was tap-selected or deselected (drives hint reset / UI). */
  onSelectChanged: (id: PlanetId | null) => void;
  /** Tap on a target while a draggable is selected (placement attempt). */
  isReducedMotion?: boolean;
}

const TAP_MOVE_TOLERANCE = 8;

export function createDragOrTap(opts: CreateDragOrTapOptions) {
  const validate =
    opts.validate ??
    ((draggableId, release, targets): PlanetId | null => {
      for (const t of targets) {
        if (isReleaseOnTarget(t.target, release, draggableId)) return t.id;
      }
      return null;
    });

  const state = new Map<
    PlanetId,
    {
      pointerStart: Point;
      lastPointer: Point;
      moved: boolean;
      dragging: boolean;
      selected: boolean;
      placed: boolean;
    }
  >();

  for (const d of opts.draggables) {
    state.set(d.id, {
      pointerStart: { x: 0, y: 0 },
      lastPointer: { x: 0, y: 0 },
      moved: false,
      dragging: false,
      selected: false,
      placed: false,
    });
    d.element.addEventListener('pointerdown', (e) => onPointerDown(d.id, e));
  }

  // Capture pointermove/up at window level so a drag continues even if the
  // pointer leaves the original element.
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp);
  window.addEventListener('pointercancel', onPointerUp);

  let disposed = false;

  function getState(id: PlanetId) {
    const s = state.get(id);
    if (!s) throw new Error(`Unknown draggable ${id}`);
    return s;
  }

  function onPointerDown(id: PlanetId, e: PointerEvent) {
    const s = getState(id);
    if (s.placed) return;
    e.preventDefault();
    const p = opts.scenePoint(e.clientX, e.clientY);
    s.pointerStart = p;
    s.lastPointer = p;
    s.moved = false;
    s.dragging = true;
    // Pickup notification (drag AND tap alike): lets the activity answer
    // with the matching-home cue and reset hint timers at the moment of
    // contact, not only after a tap-selection.
    opts.onSelectChanged(id);
    try {
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    } catch {
      /* setPointerCapture not supported — fall back to window listeners. */
    }
  }

  function onPointerMove(e: PointerEvent) {
    let anyDragging = false;
    for (const d of opts.draggables) {
      const s = getState(d.id);
      if (!s.dragging || s.placed) continue;
      anyDragging = true;
      const p = opts.scenePoint(e.clientX, e.clientY);
      const dx = p.x - s.pointerStart.x;
      const dy = p.y - s.pointerStart.y;
      if (!s.moved && Math.hypot(dx, dy) > TAP_MOVE_TOLERANCE) s.moved = true;
      s.lastPointer = p;
      const liftY = p.y - CONFIG.liftOffsetPx;
      opts.onMove(d.id, { x: p.x, y: liftY }, 'lifted');
    }
    if (!anyDragging) {
      // pointermove without an active drag is ignored.
    }
  }

  function onPointerUp(e: PointerEvent) {
    for (const d of opts.draggables) {
      const s = getState(d.id);
      if (!s.dragging || s.placed) continue;
      s.dragging = false;
      const release = opts.scenePoint(e.clientX, e.clientY);

      if (!s.moved) {
        // Treat as a tap: toggle selection. Do not attempt placement here —
        // tap-tap placement happens when the user taps a target next.
        s.selected = !s.selected;
        if (s.selected) {
          // Deselect others (single active selection).
          for (const other of opts.draggables) {
            if (other.id !== d.id) {
              const os = getState(other.id);
              if (os.selected) {
                os.selected = false;
                opts.onMove(other.id, other.home, 'rest');
              }
            }
          }
          const liftY = release.y - CONFIG.liftOffsetPx;
          opts.onMove(d.id, { x: release.x, y: liftY }, 'selected');
          opts.onSelectChanged(d.id);
        } else {
          opts.onMove(d.id, d.home, 'rest');
          opts.onSelectChanged(null);
        }
        continue;
      }

      // Drag release: validate via shared path.
      const targetId = validate(d.id, release, opts.targets);
      // Clear any lingering tap-selection so internal state matches the
      // visual state (ring removed by 'rest' transform) — otherwise an
      // invisible selection could place a planet on a later stray tap. Also
      // clears the pickup home-call cue from a pure drag (never selected).
      s.selected = false;
      opts.onSelectChanged(null);
      if (targetId) {
        commitPlacement(d, targetId);
      } else {
        opts.onMove(d.id, d.home, 'rest');
        opts.onReturned(d.id);
      }
    }
  }

  function commitPlacement(d: DraggableHandle, targetId: PlanetId) {
    const target = opts.targets.find((t) => t.id === targetId);
    if (!target) return;
    const s = getState(d.id);
    s.placed = true;
    s.selected = false;
    opts.onMove(d.id, target.target.centre, 'lifted');
    opts.onPlaced(d.id, targetId, target.target.centre);
  }

  /** Called when a target element is tapped while a draggable is selected. */
  function tapTarget(targetId: PlanetId, at: Point) {
    for (const d of opts.draggables) {
      const s = getState(d.id);
      if (s.placed || !s.selected) continue;
      const matched = validate(d.id, at, opts.targets.filter((t) => t.id === targetId));
      if (matched) {
        commitPlacement(d, matched);
        opts.onSelectChanged(null);
        return;
      }
      // Wrong target: gentle ease-back, no punitive feedback (PRP AC-06).
      opts.onMove(d.id, d.home, 'rest');
      s.selected = false;
      opts.onReturned(d.id);
      opts.onSelectChanged(null);
      return;
    }
  }

  function markPlaced(id: PlanetId) {
    const s = getState(id);
    s.placed = true;
    s.selected = false;
    s.dragging = false;
  }

  function resetAll() {
    for (const d of opts.draggables) {
      const s = getState(d.id);
      s.placed = false;
      s.selected = false;
      s.dragging = false;
      s.moved = false;
      opts.onMove(d.id, d.home, 'rest');
    }
    opts.onSelectChanged(null);
  }

  /** Remove window listeners. Idempotent — safe to call after replay reattach. */
  function dispose() {
    if (disposed) return;
    disposed = true;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerUp);
  }

  return {
    tapTarget,
    markPlaced,
    resetAll,
    dispose,
  };
}

export type DragOrTapController = ReturnType<typeof createDragOrTap>;
